/* FILE: ./frontend/src/features/canada/pages/SupplierPortal.jsx */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext'; 
import SmartListingForm from './SmartListingForm';
import { useTranslation } from 'react-i18next'; 

export default function SupplierPortal() {
  const { t } = useTranslation(); 
  const { user } = useAuth();
  const navigate = useNavigate();

  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // ⚡ BULK ACTION STATE
  const [selectedItems, setSelectedItems] = useState([]);
  const [isPublishing, setIsPublishing] = useState(false);

  const loadListings = async () => {
    if (!user) {
        setIsLoading(false);
        return;
    }
    
    try {
      setIsLoading(true);
      setError(null);

      const { data: supplier, error: supplierError } = await supabase
        .from('suppliers')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (supplierError) throw supplierError;
      
      if (!supplier) {
        setError("Supplier profile not found. Please complete onboarding.");
        setIsLoading(false);
        return;
      }

      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('supplier_id', supplier.id)
        .order('created_at', { ascending: false });

      if (productsError) throw productsError;

      setListings(products || []);
    } catch (err) {
      console.error("Inventory Sync Error:", err);
      setError("Failed to synchronize inventory. Please refresh.");
    } finally {
      setIsLoading(false);
      setSelectedItems([]); // Clear selection on load
    }
  };

  useEffect(() => {
    loadListings();
  }, [user]);

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingItem(null); 
    loadListings(); 
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingItem(null); 
    setShowForm(true);
  };

  // ⚡ BULK ACTION HANDLERS
  const toggleSelection = (id) => {
      setSelectedItems(prev => 
          prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
      );
  };

  const selectAllDrafts = () => {
      const drafts = listings.filter(l => l.stock_status === 'draft').map(l => l.id);
      setSelectedItems(drafts);
  };

  const publishSelected = async () => {
      if (selectedItems.length === 0) return;
      setIsPublishing(true);

      try {
          const { error } = await supabase
              .from('products')
              .update({ stock_status: 'in_stock' })
              .in('id', selectedItems);

          if (error) throw error;

          alert(`✅ Successfully published ${selectedItems.length} items to the marketplace!`);
          loadListings(); // Refresh grid
      } catch (err) {
          console.error("Bulk Publish Error:", err);
          alert("Failed to publish selected items.");
      } finally {
          setIsPublishing(false);
      }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 relative" data-testid="supplier-portal">
      
      {/* HEADER */}
      <div className="flex justify-between items-center mb-8 border-b border-gray-100 pb-6">
        <div>
           <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('portal.title')}</h1>
           <p className="text-sm text-gray-500 mt-1">{t('portal.subtitle')}</p>
        </div>
        
        {!showForm && (
          <button
            onClick={handleAddNew}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg shadow-sm font-semibold transition-all flex items-center gap-2"
          >
            <span>+</span> {t('portal.add_new')}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
          <div className="flex items-center">
            <span className="text-red-500 text-xl mr-2">⚠️</span>
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      {showForm ? (
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 shadow-inner animate-fade-in-up">
           <SmartListingForm onCancel={handleFormSuccess} initialData={editingItem} /> 
        </div>
      ) : (
        <>
          {isLoading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-600"></div>
            </div>
          ) : listings.length > 0 ? (
            <>
                {/* ⚡ MASS ACTION BAR: Appears when drafts exist */}
                {listings.some(l => l.stock_status === 'draft') && (
                    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col sm:flex-row justify-between items-center gap-4 shadow-sm animate-fade-in">
                        <div>
                            <span className="font-bold text-amber-900 mr-2">{t('portal.bulk_actions')}</span>
                            <span className="text-sm text-amber-800">{t('portal.drafts_count', { count: listings.filter(l => l.stock_status === 'draft').length })}</span>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={selectAllDrafts} className="px-4 py-2 text-sm text-amber-800 border border-amber-300 rounded font-medium hover:bg-amber-100 transition-colors">
                                {t('portal.select_all')}
                            </button>
                            <button 
                                onClick={publishSelected} 
                                disabled={selectedItems.length === 0 || isPublishing}
                                className="px-4 py-2 text-sm bg-amber-600 text-white rounded font-bold shadow hover:bg-amber-700 transition-colors disabled:opacity-50"
                            >
                                {isPublishing ? t('portal.publishing') : t('portal.publish_selected', { count: selectedItems.length })}
                            </button>
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
                  {listings.map((listing) => (
                    <div 
                        key={listing.id} 
                        className={`group rounded-xl shadow-sm hover:shadow-md border transition-all duration-200 p-6 flex flex-col justify-between ${
                            selectedItems.includes(listing.id) ? 'border-amber-400 bg-amber-50/50' : 'bg-white border-gray-200'
                        }`}
                    >
                       <div>
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-2">
                              {/* ⚡ CHECKBOX FOR BULK ACTION */}
                              {listing.stock_status === 'draft' && (
                                  <input 
                                      type="checkbox" 
                                      checked={selectedItems.includes(listing.id)}
                                      onChange={() => toggleSelection(listing.id)}
                                      className="h-4 w-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500 cursor-pointer"
                                  />
                              )}
                              <div className="bg-blue-50 text-blue-700 font-mono text-xs px-2 py-1 rounded shadow-sm">
                                {t('portal.hs')} {listing.hs_code || 'N/A'}
                              </div>
                          </div>
                          
                          <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wide ${
                              listing.stock_status === 'in_stock' ? 'bg-green-100 text-green-800' : 
                              listing.stock_status === 'draft' ? 'bg-amber-100 text-amber-800' :
                              'bg-yellow-100 text-yellow-800'
                          }`}>
                              {listing.stock_status?.replace('_', ' ') || t('portal.active')}
                          </span>
                        </div>
                        <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-1 group-hover:text-blue-600 transition-colors">{listing.name}</h3>
                        <div className="space-y-2 text-sm text-gray-600 mb-6">
                           <div className="flex justify-between">
                             <span>{t('portal.price')}</span>
                             <span className="font-bold text-gray-900">${listing.price_cad} CAD</span>
                           </div>
                           <div className="flex justify-between">
                             <span>{t('portal.stock')}</span>
                             <span>{listing.moq} {t('portal.moq')}</span>
                           </div>
                        </div>
                       </div>
                      
                      <div className="flex gap-2 mt-auto pt-4 border-t border-gray-100">
                        <button onClick={() => navigate(`/products/${listing.id}`)} className="flex-1 py-2 text-sm border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 bg-white transition-colors">{t('portal.view')}</button>
                        <button onClick={() => handleEditClick(listing)} className="flex-1 py-2 text-sm bg-blue-50 text-blue-700 font-medium rounded-lg hover:bg-blue-100 transition-colors">{t('portal.edit')}</button>
                      </div>
                    </div>
                  ))}
               </div>
            </>
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 animate-fade-in-up">
              <div className="text-6xl mb-4">📦</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('portal.empty_title')}</h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">{t('portal.empty_desc')}</p>
              <button onClick={handleAddNew} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-bold shadow-md transition-transform transform hover:-translate-y-0.5">
                {t('portal.create_first')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}